import { Link, useParams, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LogoFull } from "./Logo";
import { ArrowRight, Check, Zap, FileText, CreditCard, Globe, Shield, Clock, Briefcase, PenTool, Building2, Users2, Wrench, LayoutDashboard } from "lucide-react";

const INDUSTRIES = {
  "freelancers": {
    icon: Briefcase,
    title: "Invoice Software for Freelancers",
    hero: "Stop chasing payments. Start getting paid.",
    sub: "VeroBill helps freelance designers, developers, writers, and consultants create professional invoices in 30 seconds and get paid faster with 4 payment methods.",
    audience: "freelancers, independent contractors, and consultants",
    pain: "Freelancers lose an average of 10 hours per month on invoicing and payment follow-ups. That's 120 billable hours a year wasted on admin work.",
    benefits: [
      { t: "30-second invoices", d: "Create and send a professional invoice before you close your laptop. No templates to download, no spreadsheets." },
      { t: "Get paid 4 ways", d: "Stripe, PayPal, Razorpay UPI, and Wise. Your client picks their preferred method. Money goes directly to your account." },
      { t: "Unlimited estimates", d: "Send quotes to potential clients for free. When they accept, convert to an invoice in one click." },
      { t: "20+ currencies", d: "Invoice international clients in USD, EUR, GBP, INR, AUD, and more — with automatic tax calculations." },
    ],
    cta: "Start invoicing free — no credit card",
    meta: "Free invoice software for freelancers. Create professional invoices in 30 seconds with multi-currency, tax compliance, and 4 payment methods."
  },
  "contractors": {
    icon: Wrench,
    title: "Invoice Software for Contractors",
    hero: "Finish the job. Send the invoice. Get paid.",
    sub: "VeroBill is built for general contractors, subcontractors, and tradespeople who need to invoice fast — on-site or off.",
    audience: "contractors, builders, and tradespeople",
    pain: "Contractors often wait 30-60 days for payment because the invoice gets delayed. Send it the moment the job is done.",
    benefits: [
      { t: "Invoice from your phone", d: "VeroBill is a PWA — install it on your phone and invoice right from the job site. No app store needed." },
      { t: "Tax compliance built-in", d: "Auto-fill VAT, GST, Sales Tax for 12 regions. Your tax ID appears on every invoice and PDF." },
      { t: "Recurring billing", d: "Ongoing retainer or maintenance contracts? Set up recurring invoices and let VeroBill auto-send them." },
      { t: "Professional PDFs", d: "3 invoice templates with your brand color. Look professional without hiring a designer." },
    ],
    cta: "Create your first contractor invoice",
    meta: "Invoice software for contractors. Send professional invoices from your phone in 30 seconds. Tax-compliant, multi-currency, free to start."
  },
  "consultants": {
    icon: Users2,
    title: "Invoice Software for Consultants",
    hero: "Bill your expertise. Not your time on admin.",
    sub: "Whether you bill hourly, by project, or on retainer — VeroBill lets consultants create, send, and track invoices without touching a spreadsheet.",
    audience: "management consultants, business advisors, and strategy firms",
    pain: "Consultants juggle multiple clients across currencies and tax jurisdictions. Most invoicing tools make this harder, not easier.",
    benefits: [
      { t: "Multi-currency support", d: "Invoice a client in London in GBP, another in Singapore in SGD, and a third in New York in USD — all from one dashboard." },
      { t: "Estimates that convert", d: "Send a project proposal as an estimate. Client accepts? One click turns it into a draft invoice." },
      { t: "Revenue analytics", d: "See your paid vs outstanding, by client, by date range. Know your collection rate at a glance." },
      { t: "Branded PDFs", d: "Choose from 3 templates, set your brand color, add your logo. Every invoice looks like you spent hours on it." },
    ],
    cta: "Start invoicing clients for free",
    meta: "Invoice software for consultants. Multi-currency, multi-client, tax-compliant. Create professional invoices in 30 seconds."
  },
  "designers": {
    icon: PenTool,
    title: "Invoice Software for Designers",
    hero: "Design is your job. Invoicing shouldn't be.",
    sub: "VeroBill gives graphic designers, web designers, and UI/UX freelancers a fast, clean invoicing tool that matches their standards.",
    audience: "graphic designers, web designers, and creative freelancers",
    pain: "Most invoice tools look like they were designed in 2008. Designers deserve something that reflects their brand — not a generic Word template.",
    benefits: [
      { t: "3 PDF templates", d: "Classic, Modern, or Minimal. Pick the one that matches your brand. Then set your exact brand color with a hex picker." },
      { t: "Shareable client links", d: "Send a clean, public link. Clients view the invoice, download the PDF, and pay — no account needed." },
      { t: "Duplicate invoices", d: "Clone last month's invoice for a retainer client in one click. Update the date, send. Done." },
      { t: "Estimates for free", d: "Send unlimited project quotes. Clients accept or decline via a public link. Convert to invoice instantly." },
    ],
    cta: "Create a beautiful invoice in 30 seconds",
    meta: "Invoice software for designers. Beautiful PDF templates, brand colors, shareable links. Free to start, professional from day one."
  },
  "small-business": {
    icon: Building2,
    title: "Invoice Software for Small Businesses",
    hero: "Simple invoicing for businesses that don't need an accounting degree.",
    sub: "VeroBill gives small business owners the invoicing features they actually need — without the 50 features they don't.",
    audience: "small business owners, solopreneurs, and micro-agencies",
    pain: "Most small business tools charge $20-65/month for features you'll never use. VeroBill starts free and stays simple.",
    benefits: [
      { t: "Free plan, forever", d: "10 invoices/month, unlimited clients, tax templates, estimates — all free. No trial. No credit card." },
      { t: "4 payment methods", d: "Let customers pay by card (Stripe), PayPal, UPI (Razorpay), or Wise. You connect your own accounts and keep 100% of revenue." },
      { t: "12 tax regions", d: "US Sales Tax, UK VAT, EU VAT, India GST, Australia GST, and more. Auto-fill tax lines with compliance notes." },
      { t: "$59 lifetime deal", d: "Pay once, use forever. No monthly fees. Perfect for businesses watching their overhead." },
    ],
    cta: "Get started free — upgrade when you're ready",
    meta: "Free invoice software for small businesses. Tax-compliant, multi-currency, 4 payment methods. Start invoicing in 30 seconds."
  },
  "agencies": {
    icon: LayoutDashboard,
    title: "Invoice Software for Agencies",
    hero: "Invoice clients as fast as you close them.",
    sub: "VeroBill helps small agencies, creative studios, and digital shops send professional invoices without the overhead of enterprise billing software.",
    audience: "creative agencies, digital studios, and marketing firms",
    pain: "Agency billing is messy — different clients, currencies, project scopes. You need something that handles complexity without being complex.",
    benefits: [
      { t: "Unlimited clients", d: "No per-client fees. Manage as many clients as you need from one clean dashboard." },
      { t: "Estimates to invoices", d: "Send a project estimate. When the client signs off, convert it to an invoice with one click." },
      { t: "Recurring invoices", d: "Retainer clients? Set up monthly auto-invoicing. VeroBill creates and optionally sends the invoice on schedule." },
      { t: "Revenue dashboard", d: "See total revenue, outstanding amount, and collection rate filtered by client and date range." },
    ],
    cta: "Start your agency on VeroBill free",
    meta: "Invoice software for agencies. Unlimited clients, recurring invoices, estimates, and 4 payment methods. Free to start."
  },
};

export default function IndustryPage() {
  const { industry } = useParams();
  const { pathname } = useLocation();
  const data = INDUSTRIES[industry];

  // Scroll to top on page change
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex flex-col items-center justify-center gap-4">
        <p className="text-2xl font-bold text-[#0A0A0A]">Page not found</p>
        <Link to="/" className="text-sm text-[#6B7280] hover:text-[#0A0A0A]">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-[#E5E7EB] bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" data-testid="industry-logo"><LogoFull size={24} /></Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/blog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Blog</Link>
            <Link to="/help" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Help</Link>
            <Link to="/changelog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Changelog</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Sign in</Link>
            <Link to="/register" className="bg-[#0A0A0A] text-white text-[13px] font-semibold px-4 py-2 hover:bg-[#1a1a1a] transition-colors" data-testid="industry-cta-top">Get started free</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2 mb-6">
            {(() => { const Icon = data.icon; return <Icon className="w-5 h-5" />; })()}
            <span className="text-[13px] font-bold tracking-wide uppercase">{data.title}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-black text-[#0A0A0A] tracking-tight leading-[1.08] mb-6" data-testid="industry-hero-title">
            {data.hero}
          </h1>
          <p className="text-base lg:text-lg text-[#6B7280] max-w-xl mx-auto mb-10 leading-relaxed">
            {data.sub}
          </p>
          <Link to="/register" className="inline-flex items-center gap-2.5 bg-[#0A0A0A] text-white font-bold text-[15px] px-8 py-3.5 hover:bg-[#1a1a1a] transition-colors" data-testid="industry-hero-cta">
            {data.cta} <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-[13px] text-[#9CA3AF] mt-4 flex items-center justify-center gap-2">
            <Check className="w-3.5 h-3.5 text-[#10B981]" /> Free forever. No credit card required.
          </p>
        </div>
      </section>

      {/* Pain Point */}
      <section className="py-14 px-6 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-base lg:text-lg text-white/60 leading-relaxed italic">
            "{data.pain}"
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-[#0A0A0A] tracking-tight text-center mb-14" data-testid="industry-benefits-title">
            Why {data.audience} choose VeroBill
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {data.benefits.map((b, i) => (
              <div key={i} className="border border-[#E5E7EB] bg-white p-6" data-testid={`industry-benefit-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 bg-[#0A0A0A] flex items-center justify-center">
                    <span className="text-white font-bold text-[11px]">{i + 1}</span>
                  </div>
                  <h3 className="font-bold text-[15px] text-[#0A0A0A]">{b.t}</h3>
                </div>
                <p className="text-[14px] text-[#6B7280] leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Checklist */}
      <section className="py-16 px-6 bg-white border-y border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-[#0A0A0A] mb-8 text-center">Everything included</h2>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
            {[
              "Professional invoice PDFs",
              "3 PDF templates + brand color",
              "20+ currencies",
              "4 payment methods",
              "12 country tax templates",
              "Tax compliance (GSTIN, VAT, ABN)",
              "Unlimited estimates & quotes",
              "Recurring invoices",
              "Shareable public links",
              "Revenue analytics",
              "Email invoices in one click",
              "Mobile PWA (works offline)"
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2.5 py-1.5">
                <Check className="w-4 h-4 text-[#10B981] shrink-0" />
                <span className="text-[14px] text-[#374151]">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight mb-5">
            Ready to get paid faster?
          </h2>
          <p className="text-base text-[#6B7280] mb-8">
            Join thousands of {data.audience} who use VeroBill to invoice faster and get paid on time.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="inline-flex items-center gap-2.5 bg-[#0A0A0A] text-white font-bold text-[15px] px-8 py-3.5 hover:bg-[#1a1a1a] transition-colors" data-testid="industry-bottom-cta">
              {data.cta} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/lifetime" className="text-[14px] font-semibold text-[#0A0A0A] underline underline-offset-2 hover:text-[#1A3DFF]">
              Or get the $59 lifetime deal
            </Link>
          </div>
        </div>
      </section>

      {/* Other Industries */}
      <section className="py-14 px-6 bg-[#F3F4F6] border-t border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-[13px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-5 text-center">Also built for</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {Object.entries(INDUSTRIES).filter(([k]) => k !== industry).map(([key, val]) => {
              const Icon = val.icon;
              return (
                <Link
                  key={key}
                  to={`/for/${key}`}
                  className="flex items-center gap-1.5 text-[13px] text-[#6B7280] bg-white border border-[#E5E7EB] px-3 py-1.5 hover:border-[#0A0A0A] hover:text-[#0A0A0A] transition-colors"
                >
                  <Icon className="w-3.5 h-3.5" />
                  {val.title.replace("Invoice Software for ", "")}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] py-8 px-6 bg-white">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <LogoFull size={20} />
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link to="/" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Home</Link>
            <Link to="/blog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Blog</Link>
            <Link to="/help" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Help</Link>
            <Link to="/changelog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Changelog</Link>
            <span className="text-[13px] text-[#9CA3AF]">&copy; 2026 VeroBill</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { INDUSTRIES };
